import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { CORE_ACTIONS } from '@/lib/coreActions';
import { fetchModuleStats } from '@/lib/coreModules';
import { fetchFlagStats } from '@/lib/coreFeatureFlags';
import { fetchReviewStats } from '@/lib/coreReviews';
import { fetchPrivacyStats } from '@/lib/corePrivacy';
import {
  fetchCoreDashboard,
  type CoreDashboardData,
} from '@/lib/coreDashboard';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const TODAY_ITEMS: { key: keyof CoreDashboardData['today']; label: string; icon: string; accent: string }[] = [
  { key: 'appointments', label: 'Rendez-vous', icon: 'ri-calendar-check-line', accent: 'bg-primary-100 text-primary-700' },
  { key: 'sales', label: 'Ventes', icon: 'ri-money-dollar-circle-line', accent: 'bg-accent-100 text-accent-700' },
  { key: 'orders', label: 'Commandes', icon: 'ri-shopping-bag-3-line', accent: 'bg-secondary-100 text-secondary-700' },
  { key: 'payments', label: 'Paiements', icon: 'ri-bank-card-line', accent: 'bg-primary-100 text-primary-700' },
  { key: 'tasks', label: 'Tâches', icon: 'ri-checkbox-line', accent: 'bg-secondary-100 text-secondary-700' },
  { key: 'notifications', label: 'Notifications', icon: 'ri-notification-3-line', accent: 'bg-accent-100 text-accent-700' },
];

const SEVERITY: Record<string, string> = {
  warning: 'bg-accent-50 border-accent-200 text-accent-900',
  info: 'bg-secondary-50 border-secondary-200 text-secondary-900',
  success: 'bg-primary-50 border-primary-200 text-primary-900',
};

export default function CoreDashboardTab({ commerceId, org }: Props) {
  const [data, setData] = useState<CoreDashboardData | null>(null);
  const [extra, setExtra] = useState({ modules: 0, modulesEnabled: 0, flags: 0, reviews: 0, reviewsPending: 0, privacy: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, mods, flags, reviews, privacy] = await Promise.all([
        fetchCoreDashboard(org.id, commerceId, org.default_currency || 'MAD'),
        fetchModuleStats(org.id).catch(() => ({ total: 0, enabled: 0, disabled: 0, byCategory: {} })),
        fetchFlagStats(org.id).catch(() => ({ total: 0, enabled: 0, progressive: 0 })),
        fetchReviewStats(org.id).catch(() => ({ total: 0, published: 0, pending: 0, rejected: 0, reports: 0, average: 0, byRating: {} })),
        fetchPrivacyStats(org.id).catch(() => ({ total: 0, pending: 0, completed: 0, byType: {}, accessEvents: 0 })),
      ]);
      setData(d);
      setExtra({
        modules: mods.total, modulesEnabled: mods.enabled, flags: flags.enabled,
        reviews: reviews.total, reviewsPending: reviews.pending, privacy: privacy.pending,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [commerceId, org.id, org.default_currency]);

  useEffect(() => { load(); }, [load]);

  const money = (v: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: data?.currency || 'MAD', maximumFractionDigits: 0 }).format(v || 0);

  if (loading) return <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  if (error) return <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>;
  if (!data) return null;

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Dashboard central</span> : uniquement les informations pertinentes pour vous.
        Le Core n’est <span className="font-medium text-foreground-600">pas</span> un dashboard métier — les vues spécialisées appartiennent aux modules.
      </p>

      {/* Aujourd'hui */}
      <div>
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2">
          <i className="ri-sun-line text-accent-600"></i> Aujourd’hui
          <span className="text-[11px] font-normal text-foreground-400">
            {new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
          </span>
        </h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {TODAY_ITEMS.map((it) => (
            <div key={it.key} className="rounded-lg border bg-background-50 border-background-200/70 p-3">
              <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${it.accent}`}><i className={it.icon}></i></span>
              <p className="text-xl font-bold text-foreground-900 mt-2">{data.today[it.key]}</p>
              <p className="text-[11px] text-foreground-500">{it.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Totaux clés */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-user-star-line"></i></span>
          <div><p className="text-lg font-bold text-foreground-900">{data.totals.customers}</p><p className="text-xs text-foreground-500">Clients au total</p></div>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-funds-line"></i></span>
          <div><p className="text-lg font-bold text-foreground-900">{money(data.totals.revenue)}</p><p className="text-xs text-foreground-500">Revenus encaissés</p></div>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4 flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-history-line"></i></span>
          <div><p className="text-lg font-bold text-foreground-900">{data.totals.activity}</p><p className="text-xs text-foreground-500">Activité récente</p></div>
        </div>
      </div>

      {/* Actions à faire */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-todo-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Actions à faire</h4>
        </div>
        <div className="divide-y divide-background-200/60">
          {data.todos.map((t, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-3">
              <span className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${SEVERITY[t.severity]}`}><i className={t.icon}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800">{t.label}</p>
                <p className="text-[11px] text-foreground-400">{t.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Activité récente */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-pulse-line"></i></span>
            <h4 className="text-sm font-semibold text-foreground-900">Activité</h4>
          </div>
          {data.recentActivity.length === 0 ? (
            <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune activité récente.</p>
          ) : (
            <div className="divide-y divide-background-200/60">
              {data.recentActivity.map((a) => (
                <div key={a.id} className="px-4 py-2.5">
                  <p className="text-xs font-medium text-foreground-800 truncate">{a.title}</p>
                  <p className="text-[11px] text-foreground-400 truncate">
                    {a.occurred_at ? new Date(a.occurred_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    {a.amount ? ` · ${new Intl.NumberFormat('fr-FR').format(a.amount)} ${a.currency || ''}` : ''}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Prochains événements + aperçus socle */}
        <div className="space-y-4">
          <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-calendar-schedule-line"></i></span>
              <h4 className="text-sm font-semibold text-foreground-900">À venir</h4>
            </div>
            {data.upcoming.length === 0 ? (
              <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun événement planifié.</p>
            ) : (
              <div className="divide-y divide-background-200/60">
                {data.upcoming.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="w-8 h-8 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0"><i className="ri-calendar-event-line"></i></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground-800 truncate">{u.title}</p>
                      <p className="text-[11px] text-foreground-400 truncate">
                        {new Date(u.start_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {u.location ? ` · ${u.location}` : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">Aperçu du socle</h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><p className="text-lg font-bold text-foreground-900">{extra.modulesEnabled}/{extra.modules}</p><p className="text-[11px] text-foreground-500">Modules actifs</p></div>
              <div><p className="text-lg font-bold text-foreground-900">{extra.flags}</p><p className="text-[11px] text-foreground-500">Flags activés</p></div>
              <div><p className="text-lg font-bold text-foreground-900">{extra.reviews}</p><p className="text-[11px] text-foreground-500">Avis</p></div>
              <div><p className="text-lg font-bold text-accent-600">{extra.reviewsPending}</p><p className="text-[11px] text-foreground-500">Avis à modérer</p></div>
              <div><p className="text-lg font-bold text-accent-600">{extra.privacy}</p><p className="text-[11px] text-foreground-500">Confidentialité</p></div>
              <div><p className="text-lg font-bold text-foreground-900">{CORE_ACTIONS.length}</p><p className="text-[11px] text-foreground-500">Actions standard</p></div>
            </div>
          </div>
        </div>
      </div>

      <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 transition-colors">
        <i className="ri-refresh-line"></i> Actualiser le tableau de bord
      </button>
    </div>
  );
}